import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
  SimpleChanges,
  inject,
  Injector,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Actions, ofActionSuccessful, select, Store } from '@ngxs/store';
import { distinctUntilChanged, filter, fromEvent, Subscription, tap, debounceTime } from 'rxjs';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { MainTableFilterIdentification } from 'src/app/core/models/main-table-filter-identification.model';
import { AdminDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { MainTableLogicService } from 'src/app/core/services/main-table-logic.service';
import { defaultInitialPageSize } from 'src/app/core/table-config/table-paging-defaults';
import { SelectionModel } from '@angular/cdk/collections';
import {
  LoadRunTimeChildrenForTable,
  ClearRunTimeChildrenForTable,
} from 'src/app/run-time/state/run-time.actions';
import { IRunTime } from 'src/app/run-time/models/run-time.model';
import { RunTimeState } from 'src/app/run-time/state/run-time.state';
import { MainTableCustomSortingService } from '../../../core/services/main-table-custom-sorting.service';
import {
  ApplyColumnFilters,
  ApplySorting,
  ClearAllSavedFilters,
  ClearFilterDetails,
  ReFetchAndClearSorting,
  ResetFilterSearch,
  ResetFilterSearchTerm,
} from '../../../core/state/main-table-filter.actions';
import { ResizedEvent } from '../../directives/resize.directive';
import { MainTableModuleNamesEnum } from '../../enums/main-table-module-names.enum';
import { MainTableFilterState } from '../../../core/state/main-table-filter.state';
import { TableFilterStorageService } from '../../../core/services/table-filter-storage.service';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
  MainTableSortingType,
} from '../../models/main-table-filter.model';
import {
  MainTableColumnFilterComponent,
  MainTableFilterColumnData,
} from './main-table-column-filter/main-table-column-filter.component';
import { PanelConfig } from './models/panel.model';

const CHILD_RUNTIME_COLUMN = 'dropdownForRunTimeChildren__hidden';
const EXPANDABLE_COLUMNS = ['expand', CHILD_RUNTIME_COLUMN];

@Component({
  selector: 'app-main-table',
  templateUrl: './main-table.component.html',
  styleUrls: ['./main-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  exportAs: 'appMainTable',
  standalone: false,
})
export class MainTableComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);

  // This is a generic table component
  // You are obligated to specify only two properties
  // the "tableData" and "displayedColumns"
  // You can also specify optional parameters like
  // "pageSizeOptions", but it can be left as default
  @Input() tableData: any;
  @Input() displayedColumns: string[];
  @Input() detailsDisplayedColumns: string[];
  @Input() clickableColumns: string[] = [];
  /** Translation keys per column for hover tooltips on body cells */
  @Input() columnHoverTooltips: Record<string, string> = {};
  @Input() disabledColumnHeaders: string[] = [];
  @Input() customSortingColumns: string[] = [];
  @Input() filterConfig: Record<string, MainTableFilterColumnData> | null = null;
  // eslint-disable-next-line no-magic-numbers
  @Input() totalPages: any;

  // additional options

  // Option 1:
  // urlForRow as string
  @Input() urlForRow = '';
  @Input() trClass = '';
  @Input() searchingEnabled = true;
  @Input() filteringEnabled = true;
  @Input() sortingEnabled = true;
  @Input() paginationEnabled = true;
  @Input() elevationEnabled = true;
  @Input() showHeaders = true;
  @Input() showPageSizeSelector = true;
  @Input() defaultInitialPageSize = defaultInitialPageSize;
  @Input() viewMode: 'full' | 'embedded' = 'full';
  @Input() moduleNameForPaging: MainTableFilterModulesEnum;
  @Input() idFieldNameForRowHandler: string;
  @Input() trClassMap: Record<string, string> = {};

  // Option 2:
  // hasRoutingForRow is boolean for not using a routing for a row
  @Input() hasRoutingForRow = true;
  @Input() rowClickDisabled = false;
  @Input() filterPredicate: (data: any, filter: string) => boolean;
  @Input() specificTemplateName: string;
  @Input() buttonFunctions: { [key: string]: (i: number) => (...args: any[]) => void } = {};
  @Input() hideButtons = true;
  @Input() additional: any; // TODO Illia: additional is handled in multiple places, should it be just here?
  @Input() @HostBinding('class.hide-main-table-show-search') loading = false;
  @Input() initialSelection: any[] = [];
  @Input() panelConfig: PanelConfig[];
  @Input() disableSelectionByColumn = '';
  @Input() hideDisabledCheckbox = false;
  @Input() disabledCheckboxIcon = '';
  @Input() disabledCheckboxIconTooltip = '';
  @Input() columnTitlesMap: Record<string, string> = {};
  @Input() skipInitialLoad = false;
  @Input() fitToViewport = false;
  @Input() initialFilters: MainTableFilterSearchByModel[] = [];
  @Output() readonly rowHandler: EventEmitter<string | any> = new EventEmitter<string | any>();
  @Output() readonly columnHandler = new EventEmitter<{ column: string; row: any }>();
  @Output() readonly searchEmitter = new EventEmitter<string>();
  @Output() readonly selectionEmitter = new EventEmitter<any>();
  @Output() readonly customSortingEmitter = new EventEmitter<{
    column: string;
    direction: string;
  }>();
  @Output() readonly columnFiltersEmitter = new EventEmitter<
    Record<string, MainTableFilterSearchByModel[]>
  >();

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChildren(MainTableColumnFilterComponent)
  filterComponents: QueryList<MainTableColumnFilterComponent>;

  runTimeChildElements = select(RunTimeState.getRunTimeChildrenForTable);

  dataSource: MatTableDataSource<any>;
  multipleSelection = new SelectionModel<any>(true, []);
  showSelector = false;

  expandedElement: any | null;

  filterValue: string;
  sortDirection: MainTableSortingType = '';
  orderByColumn = '';
  columnFilters: Record<string, MainTableFilterSearchByModel[]> = {};
  tableColor: string;
  // Remember to add a new enum for new template on shared main-table component
  moduleRouteEnums = MainTableModuleNamesEnum;
  moduleName: string;

  currentUrl: string;
  isExpandable = false;
  needExpandableRowLoader = false;
  subheaderMaxWidth = 0;
  columnDefinitions: string[] = [];
  maintenanceSubheaderColumns = ['maintenanceJobsGroupName'];
  columnPanelMap: Record<string, PanelConfig> = {};
  expandableRowLoadingActions = [LoadRunTimeChildrenForTable];

  get hasActiveFilters(): boolean {
    return (
      this.columnFilters && Object.values(this.columnFilters).some((filters) => filters.length > 0)
    );
  }

  get hasTransientFilters(): boolean {
    return this.initialFilters && this.initialFilters.length > 0;
  }

  private subscription = new Subscription();
  private filterStateSubscription?: Subscription;
  private isApplyingSavedSort = false;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private mainTableLogicService: MainTableLogicService,
    private mainTableCustomSortingService: MainTableCustomSortingService,
    private tableFilterStorageService: TableFilterStorageService
  ) {
    const { url } = this.router;
    this.currentUrl = url;
    if (url === '/' || url === '') {
      this.moduleName = MainTableModuleNamesEnum.MainDashboard;
    }
    const urlParts = url.split(/[?\/]/g);
    urlParts.forEach((urlFragment: string) => {
      if ((<any>Object).values(this.moduleRouteEnums).includes(urlFragment)) {
        this.moduleName = urlFragment;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataSource = new MatTableDataSource(this.tableData ? this.tableData : []);
    this.setSortAndPagination();
    this.setCustomFilterPredicate();
    this.setInitiallySelectedRows();
    if (changes.displayedColumns) {
      this.columnDefinitions = changes.displayedColumns.currentValue?.filter(
        (col: string) => col !== 'multiselect'
      );
      this.showSelector = changes.displayedColumns.currentValue?.includes('multiselect');
      if (this.showSelector) {
        this.maintenanceSubheaderColumns = ['multiselect', 'maintenanceJobsGroupName'];
      }
    }
    if (changes.panelConfig) {
      this.displayedColumns.forEach((column, index) => {
        const panel = this.panelConfig.find(
          (p) => p.startIndex <= index && p.endIndex >= index
        ) as PanelConfig;
        this.columnPanelMap[column] = { ...panel };
      });
    }
  }

  ngOnInit(): void {
    this.setInitiallySelectedRows();
  }

  ngAfterViewInit(): void {
    this.setSortAndPagination();
    this.applyFilter();
    this.applySavedSorting();
    this.isExpandable = EXPANDABLE_COLUMNS.some((col) => this.displayedColumns?.includes(col));
    this.needExpandableRowLoader = this.displayedColumns?.includes(CHILD_RUNTIME_COLUMN);
    this.populateColumnFiltersFromInitial();
    this.subscription.add(
      this.actions$.pipe(ofActionSuccessful(ClearAllSavedFilters)).subscribe(() => {
        this.resetLocalFilterState();
        this.store.dispatch([
          new ReFetchAndClearSorting(this.moduleNameForPaging, this.additional),
          new ResetFilterSearch(this.moduleNameForPaging, this.additional),
          new ResetFilterSearchTerm(this.moduleNameForPaging, this.additional),
        ]);
      })
    );
  }

  private populateColumnFiltersFromInitial(): void {
    if (!this.initialFilters || this.initialFilters.length === 0) {
      return;
    }
    this.columnFilters = this.groupFiltersByColumn(this.initialFilters);
  }

  setInitiallySelectedRows(): void {
    this.multipleSelection.setSelection(
      ...(this.tableData?.filter((u: any) =>
        this.initialSelection.find(
          (s) => s[this.idFieldNameForRowHandler] === u[this.idFieldNameForRowHandler]
        )
      ) || [])
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilter(): void {
    if (!this.input) {
      return;
    }
    this.filterStateSubscription?.unsubscribe();
    const filterIdentification = new MainTableFilterIdentification(
      this.moduleNameForPaging,
      this.additional
    );
    const filterState = this.store.selectSignal(
      MainTableFilterState.getPagingDetails(filterIdentification)
    );

    this.filterStateSubscription = toObservable(filterState, { injector: this.injector }).subscribe(
      (currentFilter) => {
        this.orderByColumn = currentFilter?.orderBy || '';
        this.sortDirection = currentFilter?.ascDesc || '';
        if (Object.keys(this.columnFilters).length === 0) {
          this.columnFilters = this.groupFiltersByColumn(currentFilter?.searchBy);
        }

        if (!this.input.nativeElement.value && currentFilter?.searchTerm) {
          this.input.nativeElement.value = currentFilter.searchTerm;
        }

        this.cdr.markForCheck();
      }
    );
    this.subscription.add(this.filterStateSubscription);
    const debounceTimeMs = 500;
    this.subscription.add(
      fromEvent(this.input.nativeElement, 'keyup')
        .pipe(
          filter(Boolean),
          debounceTime(debounceTimeMs),
          distinctUntilChanged(),
          tap(() => {
            this.searchEmitter.emit(this.input.nativeElement.value);
          })
        )
        .subscribe()
    );
  }

  applyColumnFilters(column: string, filters: MainTableFilterSearchByModel[]): void {
    this.columnFilters[column] = filters;
    this.store.dispatch(
      new ApplyColumnFilters(
        this.moduleNameForPaging,
        Object.values(this.columnFilters).flat(),
        this.additional
      )
    );
    this.columnFiltersEmitter.emit(this.columnFilters);
  }

  clearFilters(): void {
    this.resetLocalFilterState();
    // Clear saved filters from localStorage for this module
    this.tableFilterStorageService.clearModuleFilters(this.moduleNameForPaging);
    this.store.dispatch(new ClearFilterDetails()).subscribe(() => {
      this.store.dispatch([
        new ReFetchAndClearSorting(this.moduleNameForPaging, this.additional),
        new ResetFilterSearch(this.moduleNameForPaging, this.additional),
        new ResetFilterSearchTerm(this.moduleNameForPaging, this.additional),
      ]);
    });
  }

  /** Clears the table search input (e.g. when a parent clears filters). */
  clearSearchField(): void {
    if (!this.searchingEnabled || !this.input?.nativeElement) {
      return;
    }
    this.input.nativeElement.value = '';
  }

  private resetLocalFilterState(): void {
    this.sortDirection = '';
    this.orderByColumn = '';
    this.filterComponents?.forEach((f) => f.resetAllControls());
    this.columnFilters = {};
    if (this.input?.nativeElement) {
      this.input.nativeElement.value = '';
    }
    if (this.sort) {
      // Set flag to prevent API request
      this.isApplyingSavedSort = true;
      this.sort.active = '';
      this.sort.direction = '';
      this.sort.sortChange.emit({
        active: '',
        direction: '',
      });
      this.isApplyingSavedSort = false;
    }
    this.columnFiltersEmitter.emit(this.columnFilters);
  }

  sortData(sort: Sort) {
    // Skip API request if we're programmatically applying saved sort state
    if (this.isApplyingSavedSort) {
      return;
    }

    let sortDirection: MainTableSortingType = '';
    if (sort.direction === 'asc') {
      sortDirection = 'Ascending';
    } else if (sort.direction === 'desc') {
      sortDirection = 'Descending';
    } else if (sort.direction === '') {
      sortDirection = '';
    }
    this.sortDirection = sortDirection;
    if (this.customSortingColumns.includes(sort.active)) {
      this.customSortingEmitter.emit({ column: sort.active, direction: this.sortDirection });
      return;
    }
    const headerName = this.getProperHeaderNameForAPI(sort.active, this.moduleNameForPaging);
    this.store.dispatch(
      new ApplySorting(this.moduleNameForPaging, headerName, sortDirection, this.additional)
    );
  }

  handleRowClick(row: any) {
    if (this.rowClickDisabled) {
      return;
    }
    if (this.urlForRow === AdminDocumentsRoutes.Structure) {
      this.router.navigate([`${this.urlForRow}/${row.id}`]);
    } else if ((!row.count || row.count === 0) && this.hasRoutingForRow) {
      if (!row.count || row.count === 0) {
        let idForRouting = '';
        if (this.idFieldNameForRowHandler) {
          const rowElement = row[this.idFieldNameForRowHandler];
          idForRouting = rowElement instanceof AbstractControl ? rowElement.value : rowElement;
        } else if (row.id) {
          idForRouting = row.id;
        }
        if (this.rowHandler.observers.length > 0) {
          this.rowHandler.emit(idForRouting);
        } else if (this.urlForRow) {
          this.router.navigate([`${this.urlForRow}/${idForRouting}`]);
        }
      }
    } else if (!this.hasRoutingForRow) {
      // return whole row object if routing is disabled to handle click manually
      this.rowHandler.emit(row);
    }
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  handleColumnClick(event: MouseEvent, column: string, row: any) {
    if (this.clickableColumns.includes(column)) {
      event.stopPropagation();
      if (column === CHILD_RUNTIME_COLUMN) {
        const filterForRunTimeChildren = new MainTableFilterModel(
          MainTableFilterModulesEnum.RunTime
        );
        filterForRunTimeChildren.pageIndex = 0;
        // eslint-disable-next-line no-magic-numbers
        filterForRunTimeChildren.pageSize = 10000;
        this.store.dispatch(new ClearRunTimeChildrenForTable());
        this.store.dispatch(new LoadRunTimeChildrenForTable(row.id, filterForRunTimeChildren));
        this.expandedElement = this.expandedElement === row ? null : row;
      } else {
        this.columnHandler.emit({ column, row });
      }
    }
  }

  handleChildColumnClick(event: { event: MouseEvent; column: string; row: any }) {
    if (this.clickableColumns.includes(event.column)) {
      event.event.stopPropagation();
      const { column } = event;
      const { row } = event;
      this.columnHandler.emit({ column, row });
    }
  }

  isExpired(expiryDate: string): boolean {
    if (this.mainTableLogicService.isExpired(expiryDate)) {
      this.tableColor = 'salmon';
      return true;
    }
    if (this.mainTableLogicService.isSoonExpired(expiryDate)) {
      this.tableColor = 'lightyellow';
      return true;
    }

    return this.mainTableLogicService.isExpired(expiryDate);
  }

  isColumnHeaderDisabled(columnHeaderName: string): boolean {
    return this.disabledColumnHeaders.some((header: string) => header === columnHeaderName);
  }

  onResize(event: ResizedEvent) {
    // set subheader max width to 80% of table width
    // can't use css because of ellipsis overflow quirks
    if (event.newRect.width > 0) {
      // eslint-disable-next-line no-magic-numbers
      this.subheaderMaxWidth = (event.newRect.width / 100) * 80;
    }
  }

  private setSortAndPagination(): void {
    if (this.sortingEnabled && this.dataSource && this.dataSource.sort) {
      this.dataSource.sortingDataAccessor = (row, key) =>
        this.mainTableCustomSortingService.getCustomSorting(row, key);
      this.dataSource.sort = this.sort;
    }
  }

  private applySavedSorting(): void {
    if (!this.sort || !this.sortingEnabled) {
      return;
    }

    const currentFilter = this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails);
    if (currentFilter?.module !== this.moduleNameForPaging) {
      return;
    }

    if (!currentFilter?.orderBy || !currentFilter?.ascDesc) {
      return;
    }

    const matSortDirection = this.convertToMatSortDirection(currentFilter.ascDesc);
    const uiColumnName = this.findUiColumnNameForApiName(currentFilter.orderBy);
    if (!uiColumnName) {
      return;
    }
    // Set flag to prevent duplicate API request
    this.isApplyingSavedSort = true;
    this.sort.active = uiColumnName;
    this.sort.direction = matSortDirection;
    this.sort.sortChange.emit({
      active: uiColumnName,
      direction: matSortDirection,
    });
    // Reset flag after emission
    this.isApplyingSavedSort = false;
  }

  private findUiColumnNameForApiName(apiName: string): string | null {
    const foundColumn = this.displayedColumns?.find((column) => {
      const mappedApiName = this.getProperHeaderNameForAPI(column, this.moduleNameForPaging);
      return mappedApiName === apiName;
    });

    return foundColumn || null;
  }

  private convertToMatSortDirection(sortingType: MainTableSortingType): 'asc' | 'desc' | '' {
    if (sortingType === 'Ascending') {
      return 'asc';
    } else if (sortingType === 'Descending') {
      return 'desc';
    }
    return '';
  }

  private setCustomFilterPredicate() {
    if (this.filterPredicate) {
      this.dataSource.filterPredicate = this.filterPredicate;
    }
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.multipleSelection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  isAllSelected() {
    const numSelected = this.multipleSelection.selected.length;
    const numRows = this.dataSource.data.filter((r) => !r[this.disableSelectionByColumn]).length;
    return numSelected === numRows;
  }

  isAnyItemSelected(): boolean {
    return this.multipleSelection.selected.length > 0;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.multipleSelection.clear();
    } else {
      this.multipleSelection.select(
        ...this.dataSource.data.filter((r) => !r[this.disableSelectionByColumn])
      );
    }

    this.emitSelection();
  }

  toggleSingle(row: any) {
    const { isSubHeader } = row;
    if (isSubHeader) {
      const groupRows = this.dataSource.data.filter((r) =>
        row.itemIds.includes(r[this.idFieldNameForRowHandler])
      );
      if (this.isAllRowsInGroupSelected(row)) {
        this.multipleSelection.deselect(row, ...groupRows);
      } else {
        this.multipleSelection.select(row, ...groupRows);
      }
      this.emitSelection();
      return;
    }
    this.multipleSelection.toggle(row);
    const rowGroupHeader = this.dataSource.data.find(
      (r) => r.isSubHeader && r.itemIds?.includes(row[this.idFieldNameForRowHandler])
    );
    if (rowGroupHeader) {
      if (this.isAllRowsInGroupSelected(rowGroupHeader)) {
        this.multipleSelection.select(rowGroupHeader);
      } else {
        this.multipleSelection.deselect(rowGroupHeader);
      }
    }
    this.emitSelection();
  }

  clearSelection() {
    this.multipleSelection.clear();
    this.emitSelection();
  }

  isAllRowsInGroupSelected(row: any): boolean {
    const { isSubHeader } = row;
    if (isSubHeader) {
      const rowsToSelect = this.dataSource.data.filter((r) =>
        row.itemIds.includes(r[this.idFieldNameForRowHandler])
      );
      return rowsToSelect.every((r) => this.multipleSelection.isSelected(r));
    }
    return false;
  }

  isSomeRowsInGroupSelected(row: any): boolean {
    const { isSubHeader } = row;
    if (isSubHeader) {
      const rowsToSelect = this.dataSource.data.filter((r) =>
        row.itemIds.includes(r[this.idFieldNameForRowHandler])
      );
      return rowsToSelect.some((r) => this.multipleSelection.isSelected(r));
    }
    return false;
  }

  private emitSelection(): void {
    this.selectionEmitter.emit(this.multipleSelection.selected.filter((r) => !r.isSubHeader));
  }

  private groupFiltersByColumn(
    filters?: MainTableFilterSearchByModel[]
  ): Record<string, MainTableFilterSearchByModel[]> {
    return (filters ?? []).reduce(
      (columnFilters: Record<string, MainTableFilterSearchByModel[]>, filter) => {
        const columnName = this.findOwningColumnForFilterProperty(filter.property);
        if (!columnFilters[columnName]) {
          columnFilters[columnName] = [];
        }
        columnFilters[columnName].push(filter);
        return columnFilters;
      },
      {}
    );
  }

  private findOwningColumnForFilterProperty(property: string): string {
    if (this.filterConfig?.[property]) {
      return property;
    }

    const matchingColumn = Object.keys(this.filterConfig ?? {}).find((columnName) =>
      this.filterConfig?.[columnName]?.sections?.some((section) => section.property === property)
    );

    return matchingColumn || property;
  }

  getProperHeaderNameForAPI(activeHeader: string, module: string): string {
    switch (activeHeader) {
      case 'arrivalDateTime':
        if (module === MainTableFilterModulesEnum.CurrentPersonnel) {
          return 'Arrival';
        }
        return 'From';
      case 'departureDateTime':
        return 'To';
      case 'expiryDate':
        return 'ExpirationDate';
      case 'currentQuantity':
        return 'Quantity';
      case 'critical':
        if (module === MainTableFilterModulesEnum.MaintenanceJobs) {
          return 'IsCriticalComponent';
        }
        return 'IsCritical';
      case 'chemical':
        return 'IsChemical';
      case 'medicineCategory':
        return 'Category';
      case 'minQuantity':
        return 'MinimumQuantity';
      case 'refrigerated':
        return 'ShouldBeRefrigerated';
      case 'departmentType':
        return 'Type';
      case 'status':
        if (module === MainTableFilterModulesEnum.CurrentPersonnel) {
          return '';
        }
        if (module === MainTableFilterModulesEnum.Trips) {
          return 'status';
        }
        return 'IsActive';
      case 'fullName':
        return 'FirstName';
      case 'phoneNumber':
        return 'Mobile';
      case 'productName':
        return 'Name';
      case 'name':
        return 'Name';
      case 'position':
        return 'Position';
      case 'readReceiptSummary':
        return 'ReadReceiptSummarySortValue';
      case 'hasReadCurrentReadReceipt':
        return 'HasReadCurrentReadReceipt';
      case 'plannedEndDate':
        return 'PlannedEnd';
      case 'totalSumVat':
        return 'TotalPrice';
      case 'employedInDepartment':
        return 'employedIn';
      case 'employedInDepartments':
        return 'employmentDepartment';
      case 'crew':
        return 'crewMembersCount';
      case 'project':
        return 'Project.Name';
      default:
        return activeHeader;
    }
  }
}
